import { maxBy, minBy } from "lodash";
import {
    CouponType,
    ExperiencePurchaseItem,
    LineItemType,
    LineItemVisibility,
    PaymentLineItemStatus,
    PaymentMethod,
    PriceDisplay,
    Product,
    ProductObject,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseObject,
    TimeslotUnit,
    UnitType,
} from "types";
import { isVisibleToTraveler } from "./LineItem";
import {
    canCancel,
    getMembershipDiscountLineItem,
    hasAffiliate,
    isExperiencePurchaseItem,
    isMembershipItem,
    isPurchaseItemCanceled,
} from "./PurchaseItem";

/**
 * Can be purchase, waitlist, timeslot, gift etc
 */
export const getCustomerName = (purchase: { customerName: string }) => {
    return purchase.customerName ?? "";
};

// Sellers can skip collecting gift organizer or recipient details, so either side may be missing.
export const getGiftPurchaserAndRecipient = (
    purchase: { customerName?: string; customerEmail?: string },
    item: { recipientName?: string; recipientEmail?: string },
) => ({
    from: [purchase.customerName, purchase.customerEmail].find(Boolean),
    to: [item.recipientName, item.recipientEmail].find(Boolean),
});

export const getPaymentLineItems = (purchase: Purchase) => {
    return (
        purchase.lineItems?.filter((lineItem) => {
            const { type } = lineItem;

            // Payment method later should not be shown  in the breakdown
            const isPayment = type === LineItemType.PAYMENT && lineItem.payment?.method !== PaymentMethod.LATER;
            // Only payment line items in “complete” state should be shown in payment summary
            const isValid = lineItem.status === PaymentLineItemStatus.COMPLETE;
            const isTravelerRefundComission =
                lineItem.type === LineItemType.REFUND_COMMISSION && lineItem.visibility === LineItemVisibility.TRAVELER;

            return (
                isValid &&
                (isPayment ||
                    isTravelerRefundComission ||
                    type === LineItemType.REFUND ||
                    type === LineItemType.AFFILIATE_DEPOSIT)
            );
        }) ?? []
    );
};

export const isMistPurchase = (purchase: Purchase) => {
    return (purchase.items?.length ?? 0) > 1;
};

export const isPackagePurchase = (purchase: Purchase) => {
    return !!purchase.items?.[0]?.parentProduct?.id;
};

/** Checks if the purchase contains any membership items */
export const isMembershipPurchase = (purchase: Purchase) => {
    return purchase.items?.some((item) => isMembershipItem(item));
};

export const getPartnerFee = (purchase: Purchase, visibility = LineItemVisibility.SELLER, includeAll = true) => {
    const visibilities = new Set([visibility, includeAll ? LineItemVisibility.ALL : undefined].filter(Boolean));
    return purchase.lineItems.find((lineItem) => {
        return lineItem.type === LineItemType.PARTNER_FEE && visibilities.has(lineItem.visibility);
    });
};

export const getPartnerFeeByTraveler = (purchase: Purchase, includeAll = true) => {
    return getPartnerFee(purchase, LineItemVisibility.TRAVELER, includeAll);
};

export const isPurchaseItemHasDate = (item: PurchaseItem) =>
    (item && "arrivalDate" in item && item.arrivalDateTime) ||
    (item && "arrivalDateTime" in item && item.arrivalDateTime);

export const isPurchaseAbandoned = (purchase: Purchase) => {
    return purchase.object === PurchaseObject.ABANDON;
};

export const isPackage = (product?: Product | PurchaseItem | null) => {
    if (!product) {
        return false;
    }

    return (product as Product).object === ProductObject.PACKAGE || isPackageItem(product as PurchaseItem);
};

export const isEveryItemCanceled = (purchase: Purchase) => {
    return purchase.items.every((item) => isPurchaseItemCanceled(item));
};

export const isEveryExperiencePurchaseItemCanceled = (purchase: Purchase) => {
    const experiencePurchaseItems = getExperiencePurchaseItems(purchase);
    return experiencePurchaseItems.every((item) => isPurchaseItemCanceled(item));
};

export const canCancelItems = (purchase: Purchase) => {
    return purchase.items.some((item) => canCancel(item));
};

export const isPackageItem = (purchaseItem?: PurchaseItem) => {
    // FIXIT: api returns nullish 'object' in 'parentProduct'
    // return purchaseItem?.parentProduct?.object === ProductObject.PACKAGE;

    // so for now lets considering that this is an OBJECT by testing only 'parentProduct' exist - not strict check, but like temporary workaround
    return !!purchaseItem?.parentProduct;
};

export const isStoreCredit = (product?: PurchaseItem | Purchase | null) => {
    if (!product) {
        return false;
    }

    return (
        ((product as Purchase).items?.[0].object ?? (product as PurchaseItem).object) ===
        PurchaseItemObject.STORE_CREDIT
    );
};

export const isGift = (item?: PurchaseItem | null) => {
    return item?.object === PurchaseItemObject.GIFT;
};

export const getTravelerItemTotal = (purchase: Purchase, item: PurchaseItem) => {
    const isMist = purchase.items.length > 1;
    let partnerFee = getPartnerFee(purchase, LineItemVisibility.TRAVELER, true);
    partnerFee = partnerFee && isVisibleToTraveler(partnerFee) ? partnerFee : undefined;

    // If this is Mist then the total is just for the amount of the item, otherwise we include partner fee
    return item.amount + (isMist ? 0 : partnerFee?.amount ?? 0);
};

export const getLowestPrice = (units?: TimeslotUnit[]) => {
    const demographics = units?.filter((unit) => unit.type === UnitType.DEMOGRAPHIC);
    return minBy(demographics, "price") ?? null;
};

export const getUnitsPrice = (priceDisplay: PriceDisplay, units?: TimeslotUnit[]) => {
    return priceDisplay === PriceDisplay.HIGHEST ? getHighestPrice(units) : getLowestPrice(units);
};

export const getHighestPrice = (units?: TimeslotUnit[]) => {
    const demographics = units?.filter((unit) => unit.type === UnitType.DEMOGRAPHIC);
    return maxBy(demographics, "price") ?? null;
};

export const getPurchaseLineItemsByType = (purchase: Purchase, type: LineItemType) => {
    return purchase.lineItems?.filter((lineItem) => type === lineItem.type) ?? [];
};

export const getExperiencePurchaseItems = (purchase: Purchase): ExperiencePurchaseItem[] => {
    return purchase.items.filter((item) => {
        return isExperiencePurchaseItem(item) && !isPurchaseItemCanceled(item);
    }) as ExperiencePurchaseItem[];
};

export const getPurchaseItemsByObject = (purchase: Purchase, objects: PurchaseItemObject | PurchaseItemObject[]) => {
    if (!Array.isArray(objects)) {
        objects = [objects];
    }

    return purchase.items.filter((item) => {
        if (isPurchaseItemCanceled(item)) {
            return false;
        }

        return objects.includes(item?.object);
    });
};

export const getExperiencePurchaseItemsHavingAffiliate = (purchase: Purchase): ExperiencePurchaseItem[] => {
    return purchase.items.filter((item) => {
        return isExperiencePurchaseItem(item) && (hasAffiliate(item) || !!item.seller);
    }) as ExperiencePurchaseItem[];
};

export const getRefundLineItems = (purchase: Purchase) => {
    return (
        purchase.lineItems?.filter(({ status, type }) => {
            return status === PaymentLineItemStatus.COMPLETE && type === LineItemType.REFUND;
        }) ?? []
    );
};

export const findItemIndex = (purchase: Purchase, item: PurchaseItem) => {
    return purchase.items.findIndex(({ id }) => id === item.id);
};

export const getVoucherLineItems = (purchase: Purchase) => {
    return purchase.lineItems.filter(({ coupon }) => coupon && coupon.type === CouponType.VOUCHER);
};

export const isPartnerPurchase = (purchase: Purchase) => {
    return (
        !!purchase.partnerSeller?.id ||
        purchase.items?.some((item) => item.seller && item.seller.id !== purchase.seller.id) ||
        purchase.lineItems?.some((lineItem) => lineItem.type === LineItemType.PARTNER_PURCHASE_ITEM)
    );
};
export const isOtherSellerPurchase = (purchase: Purchase, sellerId: ID) => {
    return purchase.seller.id !== sellerId;
};

export const isPartnerPackagePurchase = (purchase: Purchase) => {
    return purchase.items[0]?.parentProduct?.object === ProductObject.PARTNER_PACKAGE;
};

export const getPurchaseItemsHavingMembershipCodeDiscount = (purchase?: Purchase) => {
    return purchase?.items.filter((item) => !!getMembershipDiscountLineItem(item)) ?? [];
};

export const getPartnerPackageLineItems = (purchase: Purchase) => {
    return purchase.lineItems?.filter((lineItem) => lineItem.type === LineItemType.PARTNER_PACKAGE_ITEM) ?? [];
};
